import React, { useEffect, useState } from "react";
import "./userDashboard.css";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { FaUser, FaStore, FaBox, FaCoins, FaCalendarAlt, FaClock, FaMoneyBillWave, FaQrcode, FaPrint, FaDownload, FaShare, FaExchangeAlt, FaSync, FaFilePdf } from "react-icons/fa";
import { MdEmail, MdOutlineAccountCircle, MdPayment } from "react-icons/md";
import logo from "../../Assets/Banner/swasthmitra_logo.png";
import { RiCoupon2Fill } from "react-icons/ri";
import InvoiceModal from "./Invoice";
import { CiLogout } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { clearUser } from "../../Features/User/UserSlice";
import { clearCart } from "../../Features/Cart/cartSlice";
import { refreshUserProfile } from "../../Features/User/UserSlice";
import useWebSocket from "../../hooks/useWebSocket";
import RedeemCoins from "./RedeemCoins";

const UserDashboard = () => {
  const user = useSelector((state) => state.user.user);
  const medicalStore = useSelector((state) => state.user.MedicalStore);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("orders");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [totalTokensEarned, setTotalTokensEarned] = useState(0);
  const [totalReferralEarnings, setTotalReferralEarnings] = useState(0);
  const [combinedTotalEarnings, setCombinedTotalEarnings] = useState(0);
  
  // QR Code related state
  const [qrCodeData, setQrCodeData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  
  // Profile refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  
  // Initialize WebSocket for real-time updates
  const { isConnected } = useWebSocket();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const ordersResponse = await axios.get(
          `https://api.swasthmitra.com/api/transaction/getorderes/${user._id}`
        );
        setOrders(ordersResponse.data.transactions);

        const totalTokens = ordersResponse.data.transactions.reduce(
          (acc, order) => {
            const tokenPercentage = user?.tokenPercentage;
            const tokensFromOrder = Math.floor(
              (order.total * tokenPercentage) / 100
            );
            return acc + tokensFromOrder;
          },
          0
        );
        setTotalTokensEarned(totalTokens);
        
        // Calculate referral earnings
        const referralEarnings = user?.totalReferralEarnings || 0;
        setTotalReferralEarnings(referralEarnings);
        
        // Calculate combined total earnings (order tokens + referral earnings)
        const combinedTotal = totalTokens + referralEarnings;
        setCombinedTotalEarnings(combinedTotal);
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching orders:", error);
        setLoading(false);
      }
    };

    const fetchUserProfile = async () => {
      // Fetch QR code data for employees
      if (user?.role === 'employee' && user?.status === 'approved') {
        try {
          setQrLoading(true);
          const profileResponse = await axios.get(
            `https://api.swasthmitra.com/api/usermedical/user/${user._id}`
          );
          
          if (profileResponse.data.user?.qrCode) {
            setQrCodeData(profileResponse.data.user.qrCode);
          }
        } catch (error) {
          console.error("Error fetching QR code:", error);
        } finally {
          setQrLoading(false);
        }
      }
    };

    fetchOrders();
    fetchUserProfile();
  }, [user]);

  // Recalculate combined earnings when user data or order data changes
  useEffect(() => {
    if (user) {
      const referralEarnings = user?.totalReferralEarnings || 0;
      setTotalReferralEarnings(referralEarnings);
      
      const combinedTotal = totalTokensEarned + referralEarnings;
      setCombinedTotalEarnings(combinedTotal);
    }
  }, [user, totalTokensEarned]);

  // Watch for tokenPercentage changes to recalculate order earnings
  useEffect(() => {
    if (user && orders.length > 0) {
      const totalTokens = orders.reduce(
        (acc, order) => {
          const tokenPercentage = user?.tokenPercentage;
          const tokensFromOrder = Math.floor(
            (order.total * tokenPercentage) / 100
          );
          return acc + tokensFromOrder;
        },
        0
      );
      setTotalTokensEarned(totalTokens);
      console.log('🔄 Token percentage changed, recalculated order earnings:', totalTokens, 'with new percentage:', user?.tokenPercentage);
    }
  }, [user?.tokenPercentage, orders]);

  // Auto-refresh orders when user profile is updated (for coin updates)
  useEffect(() => {
    const refetchOrdersAndEarnings = async () => {
      if (!user?._id) return;
      
      try {
        // Refetch orders to get the latest data
        const ordersResponse = await axios.get(
          `https://api.swasthmitra.com/api/transaction/getorderes/${user._id}`
        );
        setOrders(ordersResponse.data.transactions);

        // Recalculate tokens earned from orders
        const totalTokens = ordersResponse.data.transactions.reduce(
          (acc, order) => {
            const tokenPercentage = user?.tokenPercentage;
            const tokensFromOrder = Math.floor(
              (order.total * tokenPercentage) / 100
            );
            return acc + tokensFromOrder;
          },
          0
        );
        setTotalTokensEarned(totalTokens);
        
        // Update referral earnings from user profile
        const referralEarnings = user?.totalReferralEarnings || 0;
        setTotalReferralEarnings(referralEarnings);
        
        // Calculate new combined total
        const combinedTotal = totalTokens + referralEarnings;
        setCombinedTotalEarnings(combinedTotal);
        
        console.log('📊 Dashboard data refreshed - Orders:', ordersResponse.data.transactions.length, 'Total earned coins:', combinedTotal);
        
      } catch (error) {
        console.error('Error refreshing dashboard data:', error);
      }
    };

    // Only refetch if user data changed (like totalReferralEarnings)
    refetchOrdersAndEarnings();
  }, [user?.totalReferralEarnings, user?.tokens]); // Watch for changes in earnings

  // Manual refresh function
  const handleRefreshProfile = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      const result = await dispatch(refreshUserProfile(user._id));
      if (result.success) {
        setLastRefresh(new Date());
        console.log('Profile refreshed manually');
      }
    } catch (error) {
      console.error('Error refreshing profile:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    dispatch(clearUser());
    dispatch(clearCart());
    navigate("/login");
  };

  // QR Code Functions
  const handlePrintQR = () => {
    if (!qrCodeData?.qrCodeDataUrl) return;
    
    // Detect if on mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
    
    if (isMobile) {
      // For mobile devices, create a better print experience
      handleMobilePrint();
    } else {
      // Desktop print experience
      handleDesktopPrint();
    }
  };

  const handleMobilePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>SwasthMitra QR Code - ${user.name}</title>
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            @page {
              size: A4 portrait;
              margin: 10mm;
            }
            
            body { 
              font-family: 'Arial', sans-serif;
              background: white;
              color: #333;
              font-size: 14px;
              line-height: 1.4;
              padding: 10px;
            }
            
            .mobile-print-container {
              max-width: 100%;
              margin: 0 auto;
              background: white;
              border: 2px solid #800080;
              border-radius: 12px;
              overflow: hidden;
            }
            
            .header-section {
              background: linear-gradient(135deg, #800080, #39B54A);
              color: white;
              padding: 20px;
              text-align: center;
            }
            
            .brand-logo {
              width: 60px;
              height: 60px;
              margin: 0 auto 10px;
              background: white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 24px;
              color: #800080;
              font-weight: bold;
            }
            
            .brand-name {
              font-size: 28px;
              font-weight: bold;
              margin-bottom: 5px;
              letter-spacing: 1px;
            }
            
            .subtitle {
              font-size: 14px;
              opacity: 0.9;
            }
            
            .employee-section {
              background: #f8f9fa;
              padding: 20px;
              text-align: center;
              border-bottom: 1px solid #dee2e6;
            }
            
            .employee-name {
              font-size: 24px;
              font-weight: bold;
              color: #333;
              margin-bottom: 5px;
            }
            
            .employee-title {
              font-size: 16px;
              color: #666;
              margin-bottom: 10px;
            }
            
            .referral-code {
              font-size: 18px;
              background: #800080;
              color: white;
              padding: 10px 20px;
              border-radius: 25px;
              display: inline-block;
              font-weight: bold;
              margin-top: 10px;
            }
            
            .qr-section {
              padding: 30px 20px;
              text-align: center;
              background: white;
            }
            
            .qr-code {
              background: white;
              padding: 20px;
              border-radius: 15px;
              border: 3px solid #800080;
              display: inline-block;
              margin-bottom: 20px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            }
            
            .qr-image {
              width: 220px;
              height: 220px;
              border-radius: 8px;
              display: block;
            }
            
            .promotional-section {
              background: linear-gradient(135deg, #e8f5e8, #f0f8ff);
              padding: 20px;
              margin: 0;
              text-align: center;
            }
            
            .main-message {
              font-size: 20px;
              font-weight: bold;
              color: #2c3e50;
              margin-bottom: 15px;
              line-height: 1.3;
            }
            
            .instructions {
              font-size: 14px;
              color: #555;
              line-height: 1.6;
              background: rgba(255,255,255,0.9);
              padding: 15px;
              border-radius: 8px;
              text-align: left;
              margin: 15px 0;
            }
            
            .instructions strong {
              color: #800080;
              font-size: 15px;
            }
            
            .contact-info {
              background: #800080;
              color: white;
              padding: 20px;
              text-align: center;
            }
            
            .website {
              font-weight: bold;
              font-size: 20px;
              margin-bottom: 10px;
            }
            
            .registration-url {
              font-size: 12px;
              opacity: 0.9;
              word-break: break-all;
              background: rgba(255,255,255,0.2);
              padding: 8px;
              border-radius: 5px;
              font-family: 'Courier New', monospace;
            }
            
            .mobile-action-buttons {
              padding: 20px;
              text-align: center;
              background: #f8f9fa;
              border-top: 1px solid #dee2e6;
            }
            
            .mobile-print-btn, .mobile-save-btn, .mobile-pdf-btn {
              background: #800080;
              color: white;
              border: none;
              padding: 15px 30px;
              border-radius: 8px;
              font-size: 16px;
              font-weight: bold;
              margin: 0 10px 10px 0;
              cursor: pointer;
              min-width: 120px;
            }
            
            .mobile-save-btn {
              background: #28a745;
            }
            
            .mobile-pdf-btn {
              background: #6c757d;
            }
            
            @media print {
              body {
                background: white !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                padding: 0 !important;
              }
              
              .mobile-print-container {
                border: none !important;
                box-shadow: none !important;
                page-break-inside: avoid;
              }
              
              .mobile-action-buttons {
                display: none !important;
              }
              
              .qr-image {
                width: 200px !important;
                height: 200px !important;
              }
            }
            
            /* Responsive adjustments for very small screens */
            @media screen and (max-width: 480px) {
              .qr-image {
                width: 180px;
                height: 180px;
              }
              
              .brand-name {
                font-size: 24px;
              }
              
              .employee-name {
                font-size: 20px;
              }
              
              .main-message {
                font-size: 18px;
              }
            }
          </style>
        </head>
        <body>
          <div class="mobile-print-container">
            <div class="header-section">
              <div class="brand-logo">🏥</div>
              <h1 class="brand-name">Swasth Mitra</h1>
              <p class="subtitle">Your Trusted Healthcare Partner</p>
            </div>
            
            <div class="employee-section">
              <div class="employee-name">${user.name}</div>
              <div class="employee-title">Swasth Mitra Representative</div>
              <div class="referral-code">Code: ${qrCodeData.referralCode}</div>
            </div>
            
            <div class="qr-section">
              <div class="qr-code">
                <img src="${qrCodeData.qrCodeDataUrl}" alt="QR Code" class="qr-image" />
              </div>
            </div>
            
            <div class="promotional-section">
              <div class="main-message">For discount scan QR register and buy product</div>
              <div class="instructions">
                <strong>How to use:</strong><br>
                1. Scan QR code with phone<br>
                2. Register on website<br>
                3. Shop with discounts
              </div>
            </div>
            
            <div class="contact-info">
              <p class="website">www.swasthmitra.com</p>
              <p class="registration-url">${qrCodeData.referralUrl}</p>
            </div>
            
            <div class="mobile-action-buttons">
              <button class="mobile-print-btn" onclick="window.print();">
                📄 Print QR Code
              </button>
              <button class="mobile-save-btn" onclick="saveMobileQR();">
                💾 Save as Image
              </button>
              <button class="mobile-pdf-btn" onclick="saveMobilePDF();">
                📋 Save as PDF
              </button>
            </div>
          </div>
          
          <script>
            function saveMobileQR() {
              // Trigger download of QR image
              const link = document.createElement('a');
              link.href = '${qrCodeData.qrCodeDataUrl}';
              link.download = 'SwasthMitra_QR_${user.name.replace(/\s+/g, '_')}_${qrCodeData.referralCode}.png';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
            
            function saveMobilePDF() {
              // For mobile PDF, use print with save as PDF instruction
              alert('To save as PDF: Use Print button and select "Save as PDF" option in your browser');
              window.print();
            }
            
            // Auto-print on mobile after content loads
            window.addEventListener('load', function() {
              setTimeout(function() {
                if (window.innerWidth <= 768) {
                  // On mobile, show the action buttons instead of auto-printing
                  document.querySelector('.mobile-action-buttons').style.display = 'block';
                } else {
                  window.print();
                }
              }, 1000);
            });
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleDesktopPrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>SwasthMitra QR Code - ${user.name}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            @page {
              size: A4 portrait;
              margin: 0;
            }
            
            @media print {
              @page {
                margin: 0;
              }
              
              body {
                margin: 0;
                padding: 0;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
            
            body { 
              font-family: 'Arial', sans-serif;
              background: white;
              color: #333;
              margin: 0;
              padding: 0;
              font-size: 12px;
              width: 100vw;
              height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            
            .print-container {
              width: 100%;
              height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 20px;
            }
            
            .qr-container { 
              width: 500px;
              background: white;
              padding: 20px;
              border-radius: 10px;
              border: 2px solid #800080;
              text-align: center;
              position: relative;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              box-shadow: 0 4px 8px rgba(0,0,0,0.1);
            }
            
            .header-section {
              padding-bottom: 15px;
              border-bottom: 2px solid #800080;
              margin-bottom: 20px;
              width: 100%;
            }
            
            .brand-logo {
              width: 50px;
              height: auto;
              margin-bottom: 8px;
            }
            
            .brand-name {
              color: #800080;
              font-size: 24px;
              font-weight: bold;
              margin: 8px 0;
              letter-spacing: 1px;
            }
            
            .subtitle {
              font-size: 12px;
              color: #666;
              margin-bottom: 10px;
            }
            
            .employee-info {
              background: linear-gradient(135deg, #800080, #39B54A);
              color: white;
              padding: 15px;
              border-radius: 8px;
              margin: 15px 0;
              width: 100%;
            }
            
            .employee-name {
              font-size: 20px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            
            .employee-title {
              font-size: 14px;
              margin: 3px 0;
              opacity: 0.95;
            }
            
            .referral-code {
              font-size: 16px;
              background: rgba(255,255,255,0.2);
              padding: 8px 15px;
              border-radius: 15px;
              margin-top: 8px;
              display: inline-block;
              font-weight: bold;
            }
            
            .qr-section {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 20px 0;
              width: 100%;
            }
            
            .qr-code {
              background: white;
              padding: 15px;
              border-radius: 10px;
              border: 3px solid #800080;
              margin: 15px 0;
              display: inline-block;
            }
            
            .qr-image {
              width: 200px;
              height: 200px;
              border-radius: 5px;
              display: block;
            }
            
            .promotional-section {
              background: linear-gradient(135deg, #e8f5e8, #f0f8ff);
              padding: 15px;
              border-radius: 8px;
              border-left: 4px solid #28a745;
              margin: 15px 0;
              width: 100%;
            }
            
            .main-message {
              font-size: 18px;
              font-weight: bold;
              color: #2c3e50;
              margin-bottom: 10px;
              text-transform: uppercase;
              letter-spacing: 1px;
              line-height: 1.2;
            }
            
            .instructions {
              font-size: 11px;
              color: #555;
              line-height: 1.4;
              background: rgba(255,255,255,0.8);
              padding: 10px;
              border-radius: 5px;
              text-align: left;
            }
            
            .instructions strong {
              color: #800080;
              font-size: 12px;
            }
            
            .contact-info {
              border-top: 2px solid #800080;
              padding-top: 15px;
              margin-top: 15px;
              width: 100%;
            }
            
            .website {
              color: #800080;
              font-weight: bold;
              font-size: 16px;
              margin-bottom: 5px;
            }
            
            .registration-url {
              font-size: 9px;
              color: #666;
              word-break: break-all;
              background: #f8f9fa;
              padding: 5px;
              border-radius: 3px;
              font-family: 'Courier New', monospace;
            }
            
            @media print {
              body {
                background: white !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              
              .print-container {
                width: 100% !important;
                height: 100vh !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                page-break-inside: avoid;
              }
              
              .qr-container {
                box-shadow: none;
                page-break-inside: avoid;
                width: 500px !important;
                margin: auto;
              }
              
              .qr-section {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="qr-container">
              <div class="header-section">
                <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiByeD0iMjUiIGZpbGw9IiNmZjZiMzUiLz4KPHN2ZyB3aWR0aD0iMjUiIGhlaWdodD0iMjUiIHg9IjEyLjUiIHk9IjEyLjUiIGZpbGw9IndoaXRlIj4KPHBhdGggZD0iTTEyLjUgN2MtMy4wMyAwLTUuNSAyLjQ3LTUuNSA1LjVzMi40NyA1LjUgNS41IDUuNSA1LjUtMi40NyA1LjUtNS41UzE1LjUzIDcgMTIuNSA3em0wIDcuNWMtMS4xIDAtMi0uOS0yLTJzLjktMiAyLTIgMiAuOSAyIDItLjkgMi0yIDJ6Ii8+Cjwvc3ZnPgo8L3N2Zz4K" alt="Swasth Mitra" class="brand-logo" />
                <h1 class="brand-name">Swasth Mitra</h1>
                <p class="subtitle">Your Trusted Healthcare Partner</p>
              </div>
              
              <div class="employee-info">
                <div class="employee-name">${user.name}</div>
                <div class="referral-code">Code: ${qrCodeData.referralCode}</div>
              </div>
              
              <div class="qr-section">
                <div class="qr-code">
                  <img src="${qrCodeData.qrCodeDataUrl}" alt="QR Code" class="qr-image" />
                </div>
              </div>
              
              <div class="promotional-section">
                <div class="main-message">For discount scan QR register and buy product</div>
                <div class="instructions">
    <strong>How to use:</strong>
    <p>1. Scan QR code with phone</p>
    <p>2. Register on website</p>
    <p>3. Shop with discounts</p>
  </div>
              </div>
              
              <div class="contact-info">
                <p class="website">www.swasthmitra.com</p>
                <p class="registration-url">${qrCodeData.referralUrl}</p>
              </div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    
    // Wait for the content to load before printing
    printWindow.onload = function() {
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    };
  };

  const handleDownloadQR = async () => {
    if (!qrCodeData?.qrCodeDataUrl) return;
    
    try {
      // Create a canvas to render the complete QR card
      await downloadCompleteQRCard();
    } catch (err) {
      console.error('Error downloading QR card:', err);
      alert('Failed to download QR card');
    }
  };

  const downloadCompleteQRCard = async () => {
    return new Promise((resolve, reject) => {
      // Create a hidden canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size for high quality
      const canvasWidth = 800;
      const canvasHeight = 1000;
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      
      // Background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      
      // Border
      ctx.strokeStyle = '#800080';
      ctx.lineWidth = 4;
      ctx.strokeRect(10, 10, canvasWidth - 20, canvasHeight - 20);
      
      let currentY = 60;
      
      // Header section
      ctx.fillStyle = '#800080';
      ctx.fillRect(20, 20, canvasWidth - 40, 120);
      
      // Brand name
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 36px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Swasth Mitra', canvasWidth / 2, currentY + 30);
      
      // Subtitle
      ctx.font = '18px Arial';
      ctx.fillText('Your Trusted Healthcare Partner', canvasWidth / 2, currentY + 60);
      
      currentY = 180;
      
      // Employee info section
      ctx.fillStyle = '#f8f9fa';
      ctx.fillRect(20, currentY, canvasWidth - 40, 100);
      
      // Employee name
      ctx.fillStyle = '#333333';
      ctx.font = 'bold 28px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(user.name, canvasWidth / 2, currentY + 35);
      
      // Employee title
      ctx.fillStyle = '#666666';
      ctx.font = '20px Arial';
      ctx.fillText('Swasth Mitra Representative', canvasWidth / 2, currentY + 65);
      
      currentY = 320;
      
      // Referral code
      ctx.fillStyle = '#800080';
      const codeWidth = 200;
      const codeHeight = 40;
      const codeX = (canvasWidth - codeWidth) / 2;
      ctx.fillRect(codeX, currentY, codeWidth, codeHeight);
      
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 18px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`Code: ${qrCodeData.referralCode}`, canvasWidth / 2, currentY + 27);
      
      currentY = 400;
      
      // Load and draw QR code
      const qrImage = new Image();
      qrImage.crossOrigin = 'anonymous';
      qrImage.onload = () => {
        // QR code border
        const qrSize = 250;
        const qrX = (canvasWidth - qrSize) / 2;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(qrX - 10, currentY - 10, qrSize + 20, qrSize + 20);
        ctx.strokeStyle = '#800080';
        ctx.lineWidth = 3;
        ctx.strokeRect(qrX - 10, currentY - 10, qrSize + 20, qrSize + 20);
        
        // Draw QR code
        ctx.drawImage(qrImage, qrX, currentY, qrSize, qrSize);
        
        currentY += qrSize + 40;
        
        // Promotional section
        ctx.fillStyle = '#e8f5e8';
        ctx.fillRect(20, currentY, canvasWidth - 40, 160);
        
        // Border for promotional section
        ctx.strokeStyle = '#28a745';
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(20, currentY);
        ctx.lineTo(20, currentY + 160);
        ctx.stroke();
        
        // Main message
        ctx.fillStyle = '#2c3e50';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('FOR DISCOUNT SCAN QR', canvasWidth / 2, currentY + 30);
        ctx.fillText('REGISTER AND BUY PRODUCT', canvasWidth / 2, currentY + 60);
        
        // Instructions
        ctx.fillStyle = '#555555';
        ctx.font = '16px Arial';
        ctx.textAlign = 'left';
        const instructionX = 60;
        ctx.fillText('How to use:', instructionX, currentY + 90);
        ctx.fillText('1. Scan QR code with phone', instructionX, currentY + 110);
        ctx.fillText('2. Register on website', instructionX, currentY + 130);
        ctx.fillText('3. Shop with discounts', instructionX, currentY + 150);
       
        
        currentY += 180;
        
        // Contact info section
        ctx.fillStyle = '#800080';
        ctx.fillRect(20, currentY, canvasWidth - 40, 80);
        
        // Website
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('www.swasthmitra.com', canvasWidth / 2, currentY + 30);
        
        // URL
        ctx.font = '12px Arial';
        const url = qrCodeData.referralUrl;
        if (url.length > 60) {
          const urlPart1 = url.substring(0, 60);
          const urlPart2 = url.substring(60);
          ctx.fillText(urlPart1, canvasWidth / 2, currentY + 50);
          ctx.fillText(urlPart2, canvasWidth / 2, currentY + 65);
        } else {
          ctx.fillText(url, canvasWidth / 2, currentY + 55);
        }
        
        // Convert canvas to blob and download
        canvas.toBlob((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `SwasthMitra_QR_Card_${user.name.replace(/\s+/g, '_')}_${qrCodeData.referralCode}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          resolve();
        }, 'image/png', 1.0);
      };
      
      qrImage.onerror = () => {
        reject(new Error('Failed to load QR code image'));
      };
      
      qrImage.src = qrCodeData.qrCodeDataUrl;
    });
  };

  // New function to save as PDF
  const handleSaveAsPDF = async () => {
    if (!qrCodeData?.qrCodeDataUrl) return;
    
    try {
      // Import html2pdf dynamically
      const html2pdf = (await import('html2pdf.js')).default;
      
      // Create a temporary div with the complete QR card content
      const element = document.createElement('div');
      element.innerHTML = `
        <div style="
          width: 210mm;
          height: 297mm;
          padding: 20mm;
          font-family: Arial, sans-serif;
          background: white;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
        ">
          <!-- Header Section -->
          <div style="
            background: linear-gradient(135deg, #800080, #39B54A);
            color: white;
            padding: 20px;
            border-radius: 15px;
            text-align: center;
            width: 100%;
            margin-bottom: 20px;
          ">
            <div style="
              width: 60px;
              height: 60px;
              background: white;
              border-radius: 50%;
              margin: 0 auto 15px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 24px;
              color: #800080;
              font-weight: bold;
            ">🏥</div>
            <h1 style="font-size: 32px; margin: 10px 0; font-weight: bold;">Swasth Mitra</h1>
            <p style="font-size: 16px; margin: 0; opacity: 0.9;">Your Trusted Healthcare Partner</p>
          </div>
          
          <!-- Employee Info -->
          <div style="
            background: #f8f9fa;
            padding: 25px;
            border-radius: 12px;
            text-align: center;
            width: 100%;
            margin-bottom: 20px;
            border: 2px solid #e9ecef;
          ">
            <h2 style="font-size: 28px; color: #333; margin-bottom: 10px; font-weight: bold;">${user.name}</h2>
            <p style="font-size: 18px; color: #666; margin-bottom: 15px;">Swasth Mitra Representative</p>
            <div style="
              background: #800080;
              color: white;
              padding: 12px 25px;
              border-radius: 25px;
              display: inline-block;
              font-size: 18px;
              font-weight: bold;
            ">Code: ${qrCodeData.referralCode}</div>
          </div>
          
          <!-- QR Code Section -->
          <div style="
            background: white;
            padding: 30px;
            border-radius: 15px;
            border: 3px solid #800080;
            text-align: center;
            margin-bottom: 20px;
            box-shadow: 0 8px 25px rgba(0,0,0,0.1);
          ">
            <img src="${qrCodeData.qrCodeDataUrl}" alt="QR Code" style="
              width: 200px;
              height: 200px;
              border-radius: 10px;
            " />
          </div>
          
          <!-- Instructions Section -->
          <div style="
            background: linear-gradient(135deg, #e8f5e8, #f0f8ff);
            padding: 25px;
            border-radius: 12px;
            border-left: 5px solid #28a745;
            width: 100%;
            margin-bottom: 20px;
          ">
            <h3 style="
              font-size: 22px;
              color: #2c3e50;
              margin-bottom: 15px;
              text-align: center;
              font-weight: bold;
            ">FOR DISCOUNT SCAN QR REGISTER AND BUY PRODUCT</h3>
            <div style="
              background: rgba(255,255,255,0.9);
              padding: 15px;
              border-radius: 8px;
              font-size: 16px;
              color: #555;
              line-height: 1.6;
            ">
              <strong style="color: #800080; font-size: 18px;">How to use:</strong><br>
              1. Scan QR code with phone<br>
              2. Register on website<br>
              3. Shop with discounts
            </div>
          </div>
          
          <!-- Contact Info -->
          <div style="
            background: #800080;
            color: white;
            padding: 20px;
            border-radius: 12px;
            text-align: center;
            width: 100%;
          ">
            <h3 style="font-size: 24px; margin-bottom: 10px; font-weight: bold;">www.swasthmitra.com</h3>
            <p style="
              font-size: 12px;
              opacity: 0.9;
              word-break: break-all;
              background: rgba(255,255,255,0.2);
              padding: 8px;
              border-radius: 5px;
              font-family: 'Courier New', monospace;
            ">${qrCodeData.referralUrl}</p>
          </div>
        </div>
      `;
      
      // Configure html2pdf options
      const opt = {
        margin: 0,
        filename: `SwasthMitra_QR_Card_${user.name.replace(/\s+/g, '_')}_${qrCodeData.referralCode}.pdf`,
        image: { type: 'jpeg', quality: 1.0 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff'
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        }
      };
      
      // Generate and save PDF
      await html2pdf().set(opt).from(element).save();
      
    } catch (err) {
      console.error('Error creating PDF:', err);
      
      // Fallback: Show instructions to manually save
      alert('To save as PDF: Use the Print button and select "Save as PDF" from your browser\'s print dialog.');
      handlePrintQR();
    }
  };

  const handleShareQR = () => {
    if (navigator.share && qrCodeData?.referralUrl) {
      navigator.share({
        title: `Join Swasth Mitra via ${user?.name}'s referral`,
        text: `Get exclusive discounts on medicines and healthcare products! Register using ${user?.name}'s referral code and start shopping with Swasth Mitra. 🏥💊`,
        url: qrCodeData.referralUrl
      }).catch(err => console.log('Error sharing:', err));
    } else {
      // Fallback: copy to clipboard
      const shareText = `Join Swasth Mitra via ${user?.name}'s referral! Get exclusive discounts on medicines and healthcare products. Register here: ${qrCodeData?.referralUrl || ''}`;
      navigator.clipboard.writeText(shareText)
        .then(() => alert('Referral information copied to clipboard!'))
        .catch(() => alert('Unable to copy referral information'));
    }
  };

  if (loading) return (
    <div className="loader-container">
      <div className="loader"></div>
      <p>Loading your dashboard...</p>
    </div>
  );

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="profile-section">
          <div className="profile-image-container">
            <img
              src={
                user?.imageUrl ||
                `https://ui-avatars.com/api/?name=${user?.name}&background=5A2094&color=fff`
              }
              alt="Profile"
              className="profile-image"
              onError={(e) => {
                e.target.src = `https://ui-avatars.com/api/?name=${user?.name}&background=5A2094&color=fff`;
              }}
            />
          </div>
          <div className="profile-details">
            <h2>
              <FaUser className="icon" /> {user?.name}
            </h2>
            <p>
              <MdEmail className="icon" /> {user?.email}
            </p>
            <p>
              <MdOutlineAccountCircle className="icon" /> {user?.role}
            </p>

            <div
  className={`status-badge1 ${user?.status}`}
  style={{
    backgroundColor: user?.status === "approved" ? "rgba(40, 167, 69, 0.1)" : "",
    color: user?.status === "approved" ? "#28a745" : "",
  }}
>
  {user?.status === "approved" ? "Active" : user?.status}
</div>

            {/* Profile Refresh Button */}
            <div className="profile-refresh-section">
              {/* <button 
                className={`refresh-button ${isRefreshing ? 'refreshing' : ''}`}
                onClick={handleRefreshProfile}
                disabled={isRefreshing}
                title="Refresh profile status"
              >
                <FaSync className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`} />
                {isRefreshing ? 'Refreshing...' : 'Refresh Status'}
              </button> */}
              {/* {lastRefresh && (
                <small className="last-refresh">
                  Last updated: {lastRefresh.toLocaleTimeString()}
                </small>
              )} */}
              
              {/* WebSocket Connection Status */}
              <div className={`websocket-status ${isConnected ? 'connected' : 'disconnected'}`}>
                {/* <span className="status-dot"></span>
                <small>
                  {isConnected ? '🟢 Real-time updates active' : '🔴 Real-time updates offline'}
                </small> */}
              </div>
            </div>


          </div>
        </div>

        <div className="tokens-card">
          <div className="tokens-header">
            <RiCoupon2Fill className="tokens-icon" />
            <h3>My Coins</h3>
          </div>
          <div className="tokens-content">
            <div className="token-item">
              <span>Percentage:</span>
              <span className="token-value">{user?.tokenPercentage}%</span>
            </div>
            <div className="token-item">
              <span>Coins Earned:</span>
              <span className="token-value">{combinedTotalEarnings}</span>
            </div>
            {user?.role === 'employee' && (
              <>
                <div className="token-item" style={{fontSize: '0.85em', opacity: '0.8'}}>
                  <span>From Orders:</span>
                  <span className="token-value">{totalTokensEarned}</span>
                </div>
                <div className="token-item" style={{fontSize: '0.85em', opacity: '0.8'}}>
                  <span>From Referrals:</span>
                  <span className="token-value">{totalReferralEarnings}</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="tabs">
          <button
            className={`tab-button ${activeTab === "orders" ? "active" : ""}`}
            onClick={() => setActiveTab("orders")}
          >
            <FaBox className="icon" /> My Orders
          </button>
          
          {/* QR Code tab - only show for employees */}
          {user?.role === 'employee' && user?.status === 'approved' && (
            <button
              className={`tab-button ${activeTab === "qrcode" ? "active" : ""}`}
              onClick={() => setActiveTab("qrcode")}
            >
              <FaQrcode className="icon" /> My QR Code
            </button>
          )}
          
          {/* Redeem Coins tab - only show for employees */}
          {user?.role === 'employee' && (
            <button
              className={`tab-button ${activeTab === "redeem" ? "active" : ""}`}
              onClick={() => setActiveTab("redeem")}
            >
              <FaExchangeAlt className="icon" /> Redeem Coins
            </button>
          )}
          
          
        </div>

        <button onClick={handleLogout} className="logout-btn">
          <CiLogout className="icon" /> Logout
        </button>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {activeTab === "orders" && (
          <div className="orders-section">
            <div className="section-header">
              <h3>
                <FaBox className="icon" /> Order History
              </h3>
              {orders.length > 0 && <div className="order-count">{orders.length} {orders.length === 1 ? 'order' : 'orders'}</div>}
            </div>
            
            {orders.length > 0 ? (
              <div className="orders-grid">
                {orders.map((order) => {
                  const createdAtDate = new Date(order.createdAt);
                  const tokenPercentage = user?.tokenPercentage;
                  const tokensEarned = Math.floor(
                    (order.total * tokenPercentage) / 100
                  );
                  
                  return (
                    <div key={order._id} className="order-card">
                      <div className="order-header">
                        <span className="order-id">#{order.orderNumber}</span>
                        <div className="header-badges">
                          <span className={`statusd ${order.status.toLowerCase()}`}>
                            {order.status === 'pending' ? '⏳' : 
                             order.status === 'processing' ? '🔄' : 
                             order.status === 'completed' ? '✅' : '❌'} {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </span>
                          <span className={`payment-method ${order.paymentMethod.toLowerCase().replace(/\s+/g, '-')}`}>
                            <MdPayment className="icon" /> {order.paymentMethod}
                          </span>
                        </div>
                      </div>
                      
                      <div className="order-details">
                        <div className="detail-item">
                          <FaCalendarAlt className="icon" />
                          <span>{createdAtDate.toLocaleDateString()}</span>
                        </div>
                        <div className="detail-item">
                          <FaClock className="icon" />
                          <span>{createdAtDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                        </div>
                        <div className="detail-item">
                          <FaMoneyBillWave className="icon" />
                          <span>₹{order.total.toFixed(2)}</span>
                        </div>
                        <div className="detail-item">
                          <FaCoins className="icon" />
                          <span>{tokensEarned} coins</span>
                        </div>
                      </div>
                      
                      {/* Product Names Section */}
                      <div className="order-products">
                        <div className="products-header">
                          <FaBox className="icon" />
                          <span className="products-label">Products ({order.orderItems?.length || 0}):</span>
                        </div>
                        <div className="products-list">
                          {order.orderItems && order.orderItems.length > 0 ? (
                            order.orderItems.slice(0, 3).map((item, index) => (
                              <div key={index} className="product-item">
                                <span className="product-name">{item.productName}</span>
                                <span className="product-quantity">x{item.quantity}</span>
                              </div>
                            ))
                          ) : (
                            <span className="no-products">No products listed</span>
                          )}
                          {order.orderItems && order.orderItems.length > 3 && (
                            <div className="more-products">
                              +{order.orderItems.length - 3} more items
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* <button
                        className="invoice-button"
                        onClick={() => setSelectedOrder(order)}
                      >
                        View Invoice
                      </button> */}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-orders">
                <img 
                  src="https://cdn.dribbble.com/users/204955/screenshots/4930541/media/75b5b2f8b6b9458b4ef7b8a9a5b4ff4b.png" 
                  alt="No orders" 
                  className="empty-state-image"
                />
                <h4>No orders yet</h4>
                <p>Your order history will appear here</p>
              </div>
            )}
          </div>
        )}

        {/* QR Code Section - Only for employees */}
        {activeTab === "qrcode" && user?.role === 'employee' && (
          <div className="qr-section">
            <div className="section-header">
              <h3>
                <FaQrcode className="icon" /> My Referral QR Code
              </h3>
            </div>
            
            {qrLoading ? (
              <div className="qr-loading">
                <div className="loader"></div>
                <p>Generating your QR code...</p>
              </div>
            ) : qrCodeData ? (
              <div className="qr-content">
                <div className="qr-info-card">
                  <div className="qr-description">
                    <h4>Share Your Referral Code</h4>
                    <p>
                      Share this QR code with potential customers. When they scan and register, 
                      you'll earn <strong>{user?.tokenPercentage}%</strong> on all their future purchases!
                    </p>
                  </div>
                  
                  <div className="referral-stats">
                    <div className="stat-item">
                      <span className="stat-label">Referral Code:</span>
                      <span className="stat-value">{qrCodeData.referralCode}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Commission Rate:</span>
                      <span className="stat-value">{user?.tokenPercentage}%</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Total Earned:</span>
                      <span className="stat-value">₹{user?.totalReferralEarnings || 0}</span>
                    </div>
                  </div>
                </div>
                
                <div className="qr-display-card">
                  <div className="qr-code-container">
                    <div className="qr-header-branding">
                      <img 
                        src={logo} 
                        alt="Swasth Mitra Logo"
                        className="qr-brand-logo"
                      />
                      <h3 className="qr-brand-name">Swasth Mitra</h3>
                    </div>
                    
                    <div className="qr-user-info">
                      <h4 className="qr-employee-name">{user?.name}</h4>
                      <p className="qr-employee-title">Swasth Mitra Representative</p>
                    </div>
                    
                    <img 
                      src={qrCodeData.qrCodeDataUrl} 
                      alt="Employee Referral QR Code"
                      className="qr-code-image"
                    />
                    
                    <div className="qr-promotional-text">
                      <p className="qr-main-message">For discount scan QR register and buy product</p>
                      <p className="qr-referral-code">Referral Code: <strong>{qrCodeData.referralCode}</strong></p>
                    </div>
                    
                    <div className="qr-code-info">
                      <p className="referral-url">
                        <small>{qrCodeData.referralUrl}</small>
                      </p>
                    </div>
                  </div>
                  
                  <div className="qr-actions">
                    {/* <button 
                      onClick={handlePrintQR}
                      className="qr-action-btn print-btn"
                      title="Print QR Code"
                    >
                      <FaPrint className="btn-icon" /> Print QR
                    </button> */}
                    <button 
                      onClick={handleDownloadQR}
                      className="qr-action-btn download-btn"
                      title="Download QR Card with Details"
                    >
                      <FaDownload className="btn-icon" /> Download Card
                    </button>
                    {/* <button 
                      onClick={handleSaveAsPDF}
                      className="qr-action-btn pdf-btn"
                      title="Save as PDF"
                    >
                      <FaFilePdf className="btn-icon" /> Save PDF
                    </button> */}
                    <button 
                      onClick={handleShareQR}
                      className="qr-action-btn share-btn"
                      title="Share Referral Link"
                    >
                      <FaShare className="btn-icon" /> Share
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-qr">
                <div className="no-qr-icon">❌</div>
                <h4>QR Code Not Available</h4>
                <p>Your account needs to be approved to generate a QR code.</p>
              </div>
            )}
          </div>
        )}

        {/* Redeem Coins Section - Only for employees */}
        {activeTab === "redeem" && user?.role === 'employee' && (
          <div className="redeem-section">
            <div className="section-header">
              <h3>
                <FaExchangeAlt className="icon" /> Redeem Coins
              </h3>
            </div>
            <RedeemCoins />
          </div>
        )}

        {activeTab === "store" && (
          <div className="store-section">
            <div className="section-header">
              <h3>
                <FaStore className="icon" /> Store Information
              </h3>
            </div>
            
            <div className="store-card">
              <div className="store-info">
                <div className="info-item">
                  <span className="info-label">Store Name:</span>
                  <span className="info-value">{medicalStore?.name || "N/A"}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Address:</span>
                  <span className="info-value">{medicalStore?.address || "N/A"}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Contact:</span>
                  <span className="info-value">{medicalStore?.mobile || "N/A"}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Employees:</span>
                  <span className="info-value">{medicalStore?.employees?.length || 0}</span>
                </div>
              </div>
              
              <div className="store-map">
                <div className="map-placeholder">
                  {medicalStore?.address ? (
                    <>
                      <div className="map-icon">📍</div>
                      <p>Map view for:<br/>{medicalStore.address}</p>
                    </>
                  ) : (
                    <p>No address available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <InvoiceModal
        selectedOrder={selectedOrder}
        onClose={() => setSelectedOrder(null)}
        user={user}
      />
    </div>
  );
};

export default UserDashboard;